/**
 * Geography Hooks
 *
 * Hooks for fetching geographic hierarchies and nodes from the API.
 */

import { useQuery } from "@tanstack/react-query";
import { geographicHierarchiesApi, geographicNodesApi, organizationApi } from "@/api";
import type { GeographicNodesParams } from "@/api/types";

/**
 * Fetch geographic hierarchies for the organization
 */
export function useGeographicHierarchies() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["geography", "hierarchies"],
    queryFn: () => geographicHierarchiesApi.list(),
    staleTime: Infinity,
  });

  return { data, loading: isLoading, error };
}

/**
 * Fetch geographic nodes with geometry for map display.
 *
 * Includes geo_version from the current organization in the request so the
 * URL acts as the cache key — bumping geo_version in the DB instantly
 * invalidates browser-cached geometry for all users.
 *
 * @param params - Parameters for fetching nodes (resolution and IDs)
 */
export function useGeographicNodesWithGeometry(params?: GeographicNodesParams) {
  const { data: org } = useQuery({
    queryKey: ["organization", "current"],
    queryFn: () => organizationApi.getOrganization(),
    staleTime: Infinity,
  });

  const paramsWithVersion: GeographicNodesParams | undefined = params && org
    ? { ...params, geo_version: org.geo_version }
    : params;

  const { data, isLoading, error } = useQuery({
    queryKey: ["geography", "nodes", paramsWithVersion],
    queryFn: () => geographicNodesApi.list(paramsWithVersion!),
    enabled: !!paramsWithVersion && !!paramsWithVersion.id && !!paramsWithVersion.resolution && org !== undefined,
    staleTime: Infinity,
  });

  return { data, loading: isLoading, error };
}

